import { type ReactNode, useEffect, useRef } from 'react'
import { X } from 'lucide-react'

// ATC-110: the close glyph here was one of five Unicode characters standing
// in for icons.
// `lucide-react` at strokeWidth 1.5 is the suite's one icon library (§1.8);
// `@fluentui/react-icons` and `@phosphor-icons/react` are banned by name.

// ─────────────────────────────────────────────────────────────────────────────
// ATC-111 — dismissal semantics, decided explicitly rather than inherited.
//
// Two gestures dismiss a dialog WITHOUT the operator aiming at anything: a
// click on the backdrop, and the Escape key. Both are low-intent — Escape in
// particular gets pressed reflexively to close a native autocomplete or to
// blur a field, with the pointer nowhere near the dialog. They are the same
// hazard wearing two costumes, so ONE property governs both. Closing only the
// backdrop would leave standing the hole that fires without the pointer being
// involved at all.
//
// The AIMED gestures always work, in every mode: the ✕ in the head, and
// whatever the consumer puts in `.modal-actions` (Cancel / Done / Confirm).
// So `deliberate` never traps anyone. WAI-ARIA APG wants Escape because a
// keyboard user must have a guaranteed exit; the ✕ is that exit — it is the
// first focusable element in the dialog, exactly one Tab from where focus
// lands on open, and the trap below guarantees that rather than inferring it
// from DOM order. What `deliberate` removes is the *unaimed* exit, not the
// exit.
//
// WHICH DIALOGS ARE `deliberate`, and why it is not only the ack-gated one:
//
//   • ConfirmRotateModal — ack-gated (the acknowledgement checkbox). Required
//     by ATC-111, and the dialog FD-ADR-038 §1.11 promoted into shared Tier 1
//     naming ATC as the standard to match. Its escalation, checkbox, impact
//     checklist and named-downstream-break copy are untouched; only the
//     response to an unaimed gesture changes.
//
//   • TokenRevealModal — NOT checkbox-gated, and yet the more expensive of the
//     two to lose. Nothing has happened when the confirm dialog is dismissed;
//     the raw token has already been minted and is unrecoverable when the
//     reveal dialog is, and recovering it means rotating again — which breaks
//     Flight Deck Manager's RUBIX_LICENSE_TOKEN a second time. Hardening only
//     the cheaper dialog would be exactly backwards, so the rule is stated by
//     consequence rather than by mechanism: a dialog is `deliberate` when
//     dismissing it destroys something the operator cannot get back. (It also
//     has a pointer-specific form of the hazard — selecting the token text and
//     releasing the mouse past the dialog edge lands the click on the
//     backdrop.)
//
// EVERYTHING ELSE STAYS `incidental` — today's behaviour, and the APG default.
// The "Issue customer environment" form is the live example: an unaimed
// dismissal costs one re-typed field and nothing has been committed. Making
// every dialog deliberate would spend the operator's patience where there is
// nothing to protect, and would devalue the signal where there is.
// ─────────────────────────────────────────────────────────────────────────────
export type ModalDismissal = 'incidental' | 'deliberate'

interface ModalProps {
  title: string
  onClose: () => void
  children: ReactNode
  /**
   * How the dialog answers an UNAIMED dismissal — backdrop click, Escape.
   *
   * - `incidental` (default): both dismiss. Nothing is lost.
   * - `deliberate`: neither dismisses. Only the ✕ and the consumer's own
   *   actions close it. Use when dismissal destroys something unrecoverable.
   */
  dismissal?: ModalDismissal
}

// Everything focusable by keyboard, in DOM order. Recomputed on every Tab
// rather than cached at open, because these dialogs change shape while they
// are up: ConfirmRotateModal's confirm button is `disabled` until the
// acknowledgement is ticked, and a cached list would either trap focus on a
// disabled control or skip the newly-enabled one.
//
// Deliberately does NOT filter on visibility. jsdom performs no layout, so
// `offsetParent` is null for every element and a visibility filter would make
// this list empty in tests — a focus trap that passes its own tests by
// trapping nothing. `:not([disabled])`, `hidden` and `aria-hidden` mean the
// same thing in both environments.
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
]
  .map((s) => `${s}:not([hidden]):not([aria-hidden="true"])`)
  .join(', ')

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
}

export function Modal({ title, onClose, children, dismissal = 'incidental' }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  // Focus in on open, focus back on close. One effect, because they are one
  // contract: the element focus came FROM is captured at the same instant
  // focus is taken, so the restore target cannot drift.
  useEffect(() => {
    const dialog = dialogRef.current
    const returnTo = document.activeElement as HTMLElement | null

    // A consumer's own `autoFocus` has already run by the time this effect
    // fires (React applies it during commit). Honour it — EnvironmentsList
    // autofocuses its customer-name input, and stealing that back to the
    // container would be a regression dressed as an accessibility fix.
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus()

    return () => {
      // The trigger may have been unmounted while the dialog was up (a row
      // re-render, a reload). `isConnected` keeps us from focusing a detached
      // node, which silently drops focus onto <body>.
      if (returnTo?.isConnected) returnTo.focus()
    }
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const dialog = dialogRef.current
      if (!dialog) return

      if (e.key === 'Escape') {
        // See the dismissal note above: Escape is the keyboard's backdrop
        // click, and is governed by the same property.
        if (dismissal === 'incidental') onClose()
        return
      }

      if (e.key !== 'Tab') return

      const focusable = focusableWithin(dialog)
      const active = document.activeElement

      // A dialog with nothing focusable in it: hold focus on the container
      // rather than let Tab walk into the page behind, which is inert to the
      // pointer but not to the keyboard.
      if (focusable.length === 0) {
        e.preventDefault()
        dialog.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      // Only the BOUNDARIES are intercepted; the browser moves focus through
      // the middle. Driving every step by hand would override composite-widget
      // behaviour (radio groups, date inputs) for no gain.
      //
      // The container is a boundary in BOTH directions. It is where focus
      // lands on open and it is not itself in the tab order, so leaving either
      // direction to the browser would make the first keystroke of every
      // dialog depend on the container happening to precede its children in
      // DOM order. Driving both explicitly is one condition, and it is what
      // makes "the ✕ is one Tab away" a property this component guarantees
      // rather than one it hopes for — which is the whole basis for
      // `deliberate` withholding Escape.
      if (e.shiftKey) {
        if (active === first || active === dialog || !dialog.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || active === dialog || !dialog.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, dismissal])

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        // Only a click that lands ON the backdrop — not one that bubbled out
        // of the dialog — and only where dismissal is incidental.
        if (e.target === e.currentTarget && dismissal === 'incidental') onClose()
      }}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Programmatically focusable, never in the tab order: this is where
        // focus lands on open, so the dialog's accessible name is announced
        // before its contents and Tab starts from the top.
        tabIndex={-1}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn-link modal-close" onClick={onClose} aria-label="Close">
            {/* Decorative: the button's accessible name is `aria-label`. */}
            <X size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}
